/**
 * Phase 249 — Year-End Fee Report Card (reusable).
 *
 * One component, two postures:
 *
 *   <YearEndReportCard mode="owner" />          → Finance dashboard.
 *      ▸ FY picker (current FY + previous 4 FYs).
 *      ▸ Student picker (auto-loaded from /api/students).
 *      ▸ Download single PDF | Download all (bulk ZIP).
 *
 *   <YearEndReportCard mode="self" studentId={x} />  → Student Portal billing tab.
 *      ▸ FY picker (current FY + previous 4 FYs).
 *      ▸ "Download My Statement" button only.
 *
 * Uses the existing /lib/downloadDocument helper so we inherit the same
 * PDF → HTML fallback story the rest of the platform uses.
 */
import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { downloadDocument } from '@/lib/downloadDocument';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Download, Loader2, Archive, Calendar } from 'lucide-react';
import { toast } from 'sonner';

/** Generate the 5 most recent Indian fiscal years (current + 4 priors).
 *  Indian FY runs April 1 → March 31, so before April we still belong
 *  to the FY that started LAST April. */
function _availableFiscalYears() {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const years = [];
  for (let i = 0; i < 5; i++) {
    const s = startYear - i;
    const e = (s + 1) % 100;
    years.push({ value: `${s}-${String(e).padStart(2, '0')}`, label: `FY ${s}–${String(e).padStart(2, '0')}` });
  }
  return years;
}

export function YearEndReportCard({ mode = 'owner', studentId: forcedStudentId = null, defaultStudentId = null }) {
  const fyOptions = useMemo(() => _availableFiscalYears(), []);
  const [fy, setFy] = useState(fyOptions[0].value);
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(forcedStudentId || defaultStudentId || '');
  const [downloading, setDownloading] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Owner mode — load the student list so the picker can offer all kids.
  useEffect(() => {
    if (mode !== 'owner') return;
    (async () => {
      try {
        const r = await api.get('/students');
        const list = Array.isArray(r.data) ? r.data : (r.data?.students || []);
        setStudents(list);
        if (!studentId && list.length > 0) {
          setStudentId(list[0].id);
        }
      } catch (err) {
        // Owners on a fresh academy may have 0 students — don't toast loud.
      }
    })();
    // We deliberately do NOT depend on studentId here so user picks aren't overridden.
  }, [mode]);

  const studentLabel = useMemo(() => {
    if (mode !== 'owner') return '';
    const s = students.find((x) => x.id === studentId);
    return s?.name || '';
  }, [mode, students, studentId]);

  const handleDownloadSingle = async () => {
    const sid = forcedStudentId || studentId;
    if (!sid) {
      toast.error('Pick a student first');
      return;
    }
    setDownloading(true);
    try {
      const safeName = (studentLabel || 'Student').replace(/[^\w-]+/g, '_');
      await downloadDocument(
        api,
        `/finance/reports/year-end/${sid}/pdf?fy=${fy}`,
        `YearEnd_FY${fy}_${safeName}.pdf`,
        { label: 'Year-End Fee Report' },
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadBulk = async () => {
    setBulkDownloading(true);
    try {
      const res = await api.get(`/finance/reports/year-end-bulk?fy=${fy}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `YearEnd_FY${fy}_AllStudents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      const rendered = res.headers?.['x-total-rendered'];
      const fallback = res.headers?.['x-pdf-fallback-count'];
      toast.success(
        `Bulk archive ready — ${rendered || '?'} student${rendered === '1' ? '' : 's'} included` +
          (Number(fallback) > 0 ? ` (${fallback} as HTML fallback)` : ''),
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Bulk download failed';
      toast.error(detail);
    } finally {
      setBulkDownloading(false);
    }
  };

  return (
    <Card data-testid="year-end-report-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <FileText className="h-4 w-4 text-emerald-600" />
          Year-End Fee Report
          <span className="text-xs font-normal text-muted-foreground">· Tax-season ledger</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className={`grid gap-3 ${mode === 'owner' ? 'sm:grid-cols-2' : ''}`}>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Fiscal Year
            </Label>
            <Select value={fy} onValueChange={setFy}>
              <SelectTrigger data-testid="year-end-fy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fyOptions.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mode === 'owner' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Student</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger data-testid="year-end-student-select">
                  <SelectValue placeholder={students.length === 0 ? 'No students yet' : 'Pick a student'} />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.custom_id ? ` · ${s.custom_id}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            onClick={handleDownloadSingle}
            disabled={downloading || (mode === 'owner' && !studentId)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="year-end-download-single"
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            {mode === 'self' ? 'Download My Statement' : 'Download PDF'}
          </Button>
          {mode === 'owner' && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadBulk}
              disabled={bulkDownloading}
              data-testid="year-end-download-bulk"
            >
              {bulkDownloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Archive className="h-3.5 w-3.5 mr-1.5" />}
              Download All (ZIP)
            </Button>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Includes tuition invoices, payments received, and scholarship adjustments for the selected Indian
          fiscal year (Apr 1 → Mar 31). Bulk archives skip students with zero financial activity.
        </p>
      </CardContent>
    </Card>
  );
}

export default YearEndReportCard;
